import * as React from 'react';
import * as autoBind from 'react-autobind';
import StartReviewModal from '../StartReviewModal/StartReviewModal';
import TrialModalContainer from '../TrialModal/TrialModalContainer';
import DeleteReviewModalContainer from '../DeleteReviewModal/DeleteReviewModalContainer';
import BuyNowModalContainer from '../BuyNowModal/BuyNowModalContainer';
import Constants, {ModalStates} from '../../globals/Constants';
import ModalEventHandlersModel, {nullModalEventHandlersFactory} from '../../types/ModalEventHandlers';
import ReviewerGroupModel from '../../types/ReviewerGroupModel';
import ReviewerGroupsModal from '../ReviewerGroupsModal/ReviewerGroupsModal';
import HotjarService from '../../services/HotjarService/HotjarService';
import Themeable from '../../types/Themeable';
import ShareReviewModalContainer from '../ShareReviewModal/ShareReviewModalContainer';
import ReviewModel from '../../types/ReviewModel';
import ManageReviewersModal from '../ManageReviewersModal/ManageReviewersModal';
import ChangeReviewByDateNotificationsModal from '../ChangeReviewByDateNotificationsModal/ChangeReviewByDateNotificationsModal';
import SimpleReviewerModel from '../../types/SimpleReviewerModel';
import SelfIdentifyModal from '../SelfIdentifyModal/SelfIdentifyModal';
import EditDescriptionModal from '../EditDescriptionModal/EditDescriptionModal';
import EditTitleModal from '../EditTitleModal/EditTitleModal';
import LeaveReviewModal from '../LeaveReviewModal/LeaveReviewModal';
import {RouteComponentProps} from 'react-router-dom';

export default class ModalManager extends React.Component<ModalManagerProps, {}> {
   constructor(props: ModalManagerProps) {
      super(props);
      autoBind(this);
   }

   componentDidMount() {
      this.setBodyPreventScroll(this.props.modalState);
   }

   componentWillUnmount(): void {
      this.props.setModalState(ModalStates.closed, nullModalEventHandlersFactory());
   }

   componentDidUpdate(prevProps: ModalManagerProps) {
      if (this.props.modalState !== prevProps.modalState) {
         this.setBodyPreventScroll(this.props.modalState);
         this.props.modalState !== ModalStates.closed ? HotjarService.hideWidget() : HotjarService.showWidget();
      }
   }

   setBodyPreventScroll(modalState: ModalStates): void {
      if (modalState !== ModalStates.closed) {
         window.document.getElementsByTagName('body')[0].classList.add(Constants.preventScrollClassName);
         document.activeElement && (document.activeElement as HTMLElement).blur();
      } else {
         window.document.getElementsByTagName('body')[0].classList.remove(Constants.preventScrollClassName);
      }
   }

   onClose(): void {
      this.props.setModalState(ModalStates.closed, nullModalEventHandlersFactory());
   }

   render() {
      const {modalState, reviewerGroups, themeName, review, setModalState, history, versionNumber, isOwner} = this.props;
      const {trialThenStartReview, trialOnly, newReview, manageReviewerGroups, deleteReview, deleteReviewWithComments, buyNow, shareReview, changeReviewByDate, manageReviewers, selfIdentify, shareReviewWithCreatedHeader, newVersion, editReviewTitle, editReviewDescription, leaveReview} = ModalStates;
      const filteredReviewers = review && review.Reviewers.filter(reviewer => reviewer.Role !== Constants.reviewerRoles.owner && !reviewer.WasUserRoleRemoved) || [];
      const reviewers: SimpleReviewerModel[] = filteredReviewers.map(reviewer => ({Email: reviewer.EmailAddress, Status: Constants.reviewerStatus.added, Id: reviewer.Id, Notify: false}));
      const showStartReviewModal = modalState === newReview || modalState === newVersion;
      const showManageReviewers = modalState === manageReviewers;
      const showReviewerGroupsModal = modalState === manageReviewerGroups;
      const showTrialModal = modalState === trialOnly || modalState === trialThenStartReview;
      const showDeleteModal = modalState === deleteReview || modalState === deleteReviewWithComments;
      const showLeaveModal = modalState === leaveReview;
      const showBuyNowModal = modalState === buyNow;
      const showShareReview = review && modalState === shareReview && isOwner;
      const showShareReviewWithCreatedHeader = review && modalState === shareReviewWithCreatedHeader && isOwner;
      const showChangeReviewByDateNotificationsModal = modalState === changeReviewByDate && isOwner;
      const showSelfIdentifyModal = modalState === selfIdentify;
      const showEditReviewTitle = review && modalState === editReviewTitle && isOwner;
      const showEditReviewDescription = review && modalState === editReviewDescription && isOwner;

      return (
         <React.Fragment>
            <StartReviewModal themeName={themeName} visible={showStartReviewModal} onClose={this.onClose} modalState={modalState} setModalState={setModalState} review={review} history={history} />
            {showManageReviewers && <ManageReviewersModal themeName={themeName} onClose={this.onClose} title={review.Title} reviewers={reviewers} reviewId={review.Id} isOwner={isOwner} versionNumber={versionNumber} />}
            {showShareReview && <ShareReviewModalContainer themeName={themeName} onClose={this.onClose} setModalState={setModalState} reviewers={reviewers} isOwner={true} showReviewCreated={false} history={history} />}
            {showShareReviewWithCreatedHeader && <ShareReviewModalContainer themeName={themeName} onClose={this.onClose} setModalState={setModalState} reviewers={reviewers} history={history} isOwner={true} showReviewCreated={true} />}
            {showReviewerGroupsModal && <ReviewerGroupsModal themeName={themeName} onClose={this.onClose} reviewerGroups={reviewerGroups} />}
            {showTrialModal && <TrialModalContainer onClose={this.onClose} startNewReview={modalState === ModalStates.trialThenStartReview} />}
            {showDeleteModal && <DeleteReviewModalContainer reviewHasComments={modalState === deleteReviewWithComments} onClose={this.onClose} />}
            {showLeaveModal && <LeaveReviewModal onClose={this.onClose} />}
            {showBuyNowModal && <BuyNowModalContainer onClose={this.onClose} />}
            {showChangeReviewByDateNotificationsModal && <ChangeReviewByDateNotificationsModal onClose={this.onClose} />}
            {showSelfIdentifyModal && <SelfIdentifyModal themeName={themeName} onClose={this.onClose} />}
            {showEditReviewDescription && <EditDescriptionModal themeName={themeName} visible={showEditReviewDescription} review={review} onClose={this.onClose} />}
            {showEditReviewTitle && <EditTitleModal themeName={themeName} visible={showEditReviewTitle} review={review} onClose={this.onClose} />}
         </React.Fragment>
      );
   }
}

export interface ModalManagerProps extends RouteComponentProps, Themeable {
   modalState: ModalStates;
   reviewerGroups: ReviewerGroupModel[];
   review: ReviewModel;
   versionNumber: number;
   isOwner: boolean;
   setModalState(modalState: ModalStates, modalEventHandlersModel: ModalEventHandlersModel): void;
}
